/**
 * How the SPA performs the OIDC back-channel (token exchange, refresh,
 * userinfo). Front-channel (authorize redirect) is always browser-direct.
 *
 * - `browser-direct` — IdP supports SPA CORS + public clients (e.g. Entra ID).
 * - `token-proxy`    — Back-channel goes through our backend, which attaches
 *                      the client secret server-side (e.g. PingFederate).
 *                      Default for enterprise IdPs. See design decision D4.
 */
export type IdpAuthFlow = "browser-direct" | "token-proxy";

export const IDP_AUTH_FLOWS: readonly IdpAuthFlow[] = ["browser-direct", "token-proxy"] as const;

/**
 * Identity Provider configuration.
 *
 * Stored per-account in the database. A row with `accountId = null` is the
 * global/platform IdP (seeded fallback). Multiple IdPs coexist on one
 * deployment; the set of active issuers is the JWT trust allowlist (D3).
 */
export interface IdpConfig {
	/** Unique key for this IdP configuration. */
	id: string;
	/** The account this IdP config belongs to. `null` = global/platform-level. */
	accountId: string | null;
	/** Display label (e.g. "Forvia PingFederate", "Entra ID CIAM"). */
	label: string;
	/** OIDC issuer URI. Used for JWT verification and discovery. */
	issuer: string;
	/** JWKS URI — overrides auto-discovery from issuer. */
	jwksUri: string | null;
	/** Token introspection endpoint (RFC 7662) for opaque tokens. */
	introspectionEndpoint: string | null;
	/** Userinfo endpoint for BFF proxy. */
	userinfoEndpoint: string | null;
	/** Token endpoint for the proxy token exchange. */
	tokenEndpoint: string | null;
	/** OAuth2 client ID. */
	clientId: string | null;
	/** OAuth2 client secret (stored encrypted at rest — never returned by API). */
	clientSecret: string | null;
	/** OAuth2 audience / scope. */
	audience: string | null;
	/** Whether issuer verification should be skipped (debugging only). */
	skipIssuerCheck: boolean;
	/** Back-channel flow for the SPA (see IdpAuthFlow). */
	authFlow: IdpAuthFlow;
	/** Whether this config is active. */
	active: boolean;
	createdAt: Date;
	updatedAt: Date;
}

/** Shape for creating a new IdP config. */
export interface CreateIdpConfig {
	accountId?: string | null;
	label: string;
	issuer: string;
	jwksUri?: string | null;
	introspectionEndpoint?: string | null;
	userinfoEndpoint?: string | null;
	tokenEndpoint?: string | null;
	clientId?: string | null;
	clientSecret?: string | null;
	audience?: string | null;
	skipIssuerCheck?: boolean;
	authFlow?: IdpAuthFlow;
}

/** Shape for updating an IdP config. */
export interface UpdateIdpConfig {
	label?: string;
	issuer?: string;
	jwksUri?: string | null;
	introspectionEndpoint?: string | null;
	userinfoEndpoint?: string | null;
	tokenEndpoint?: string | null;
	clientId?: string | null;
	clientSecret?: string | null;
	audience?: string | null;
	skipIssuerCheck?: boolean;
	authFlow?: IdpAuthFlow;
	active?: boolean;
}

/**
 * Public login configuration returned by home-realm discovery
 * (`GET /api/auth/login-config?email=…`). Contains only public values —
 * never the client secret. For `token-proxy` IdPs, `tokenEndpoint` and
 * `userinfoEndpoint` point at our backend proxy instead of the IdP.
 */
export interface PublicLoginConfig {
	idpConfigId: string;
	issuer: string;
	clientId: string | null;
	authFlow: IdpAuthFlow;
	tokenEndpoint: string | null;
	userinfoEndpoint: string | null;
	audience: string | null;
}

/**
 * Build the OAuth scope for an authorize or refresh request.
 *
 * Entra (login.microsoftonline.com / ciamlogin.com issuers):
 * - A bare audience is not a valid scope (AADSTS70011); it needs /.default
 *   or a named scope suffix.
 * - When the audience equals the client id, the resource must be the GUID,
 *   not api://<id> (AADSTS90009).
 * - Scope names are registration-defined, so request /.default instead of
 *   guessing a name (AADSTS65005). An audience that already names a scope
 *   path is used verbatim.
 * Other IdPs use the audience verbatim as scope (e.g. PingFederate).
 */
export function buildOauthScope(
	baseScope: string,
	cfg: Pick<PublicLoginConfig, "issuer" | "clientId" | "audience">,
): string {
	if (!cfg.audience) return baseScope;
	const isEntra = /(^|\.)(login\.microsoftonline\.com|ciamlogin\.com)\//.test(cfg.issuer.replace(/^https?:\/\//, ""));
	if (!isEntra) return `${baseScope} ${cfg.audience}`;
	const bare = cfg.audience.replace(/^api:\/\//, "");
	// Audience already names a scope path -> use verbatim.
	if (bare.includes("/")) return `${baseScope} ${cfg.audience}`;
	// Self-call: GUID-based identifier. Separate API app: api:// URI.
	const resource = bare === cfg.clientId ? bare : `api://${bare}`;
	return `${baseScope} ${resource}/.default`;
}

/**
 * Auth strategy types supported by the platform.
 * Used to select which strategy handles a given request.
 */
export type AuthStrategyType = "api-key" | "jwt-oidc" | "opaque-introspect" | "local-auth";
